// Game flow (RALLY_GOAL.md §13): menu with the car showcase → car on the start line, the co-driver
// reads the opening notes → countdown lights (false start = penalty) → stage with splits, resets
// (+penalty) and recovery prompts → flying finish → results with personal best → restart or menu.
// Pause menu, auto-pause on focus loss, settings applied live and saved.
import type { DriveSession } from './session';
import type { ControlInput } from '../sim/types';
import type { InputDevices } from '../input/devices';
import type { AudioMixer } from '../audio/mixer';
import type { CarAudio } from '../audio/car-audio';
import type { CodriverRuntime } from '../codriver/runtime';
import type { Hud } from '../ui/hud';
import type { GcGuard } from './gc-guard';
import type { TouchControls } from '../ui/touch';
import { PadNav, type Screens } from '../ui/screens';
import { PadCapture, type CaptureKind, type CaptureResult, type PadSnapshot } from '../input/capture';
import { controlsScreen, finishScreen, mainMenu, pauseScreen, toast, touchNotice, type MenuContext } from '../ui/menus';
import { qualityFor, type Settings, type SettingsStore } from './settings';
import { StageTiming, RESET_PENALTY, LIGHTS, LIGHT_INTERVAL, formatTime, type BestEntry, type BestTimesStore } from './timing';
import type { PresetName, QualitySettings } from '../render/quality';
import { GhostRecorder, type GhostPose, type GhostStore, type GhostTrack } from './ghost';
import type { GhostView } from '../render/ghost-view';
import type { CarWear } from '../render/car-wear';
import type { SurfaceId } from '../sim/types';
import type { StageSpec } from '../world/types';

export type FlowState = 'menu' | 'drive' | 'results';

/** Timing lines of a stage (the hidden test pad has none: it only runs in direct mode). */
export function stageLayout(spec: StageSpec): { startS: number; splits: number[]; finishS: number; stopS: number } {
  if (spec.kind === 'road') return { startS: spec.startDistance, splits: spec.splits, finishS: spec.finishDistance, stopS: spec.stopDistance };
  return { startS: 0, splits: [], finishS: Number.POSITIVE_INFINITY, stopS: Number.POSITIVE_INFINITY };
}

/** Car held in place (menu, grid): brake below the 0.5 that selects reverse at a standstill, plus the handbrake. */
const HOLD: ControlInput = { steer: 0, throttle: 0, brake: 0.4, handbrake: 1, shiftUp: false, shiftDown: false };
const COUNT_WORDS = ['five', 'four', 'three', 'two', 'one'];
/** Grid position: the car's centre this far behind the start line (front bumper ≈ 1 m back; whole metres, see placeAtDistance). */
const GRID_BACK = 3;

export interface FlowDeps {
  session: DriveSession;
  devices: InputDevices;
  mixer: AudioMixer;
  carAudio: CarAudio;
  codriver: CodriverRuntime | null;
  hud: Hud;
  touch: TouchControls | null;
  screens: Screens;
  ui: HTMLElement;
  store: SettingsStore;
  settings: Settings;
  detectedPreset: PresetName;
  /** URL overrides on top of the player's graphics settings (?quality, ?benchmark, ?nodynres). */
  qualityOverride: (q: QualitySettings) => QualitySettings;
  /** Called after the quality changes (pixel ratio follows the preset). */
  onQuality: (q: QualitySettings) => void;
  best: BestTimesStore;
  ghosts: GhostStore;
  ghostView: GhostView | null;
  /** Cosmetic damage and dirt (null without a car model). */
  wear: CarWear | null;
  stageId: string;
  carId: string;
  stageName: string;
  carName: string;
  carLine: string;
  /** Visible content for the menu selectors. */
  cars: { id: string; name: string }[];
  stages: { id: string; name: string }[];
  buildId: string;
  title: string;
  tagline: string;
  touchOnly: boolean;
  /** Benchmark: never pause on focus loss. */
  bench: boolean;
  /** Keeps full GCs out of stage runs (start-line checkpoint). */
  gc: GcGuard;
  /** Dev/test (?bot=<kind>): a bot drives the stage in the normal flow, from the green light. */
  testBot?: (dt: number) => ControlInput;
}

export class GameFlow {
  state: FlowState = 'menu';
  paused = false;
  readonly timing: StageTiming;
  private readonly d: FlowDeps;
  private readonly padNav = new PadNav();
  private readonly gridS: number;
  private readonly stopS: number;
  private gridT = 0;
  /** The start prompt is showing (waiting for the GC checkpoint / memory reducer). */
  private startWait = false;
  /** Start-line GC checkpoint still to run (src/game/gc-guard.ts). */
  private gcPending = false;
  private prevBest: BestEntry | null = null;
  private finishedAt = -1;
  private stoppedT = 0;
  private flipT = 0;
  private oobT = 0;
  private stuckT = 0;
  private hornOn = false;
  private lastSurface = '';
  private lastQuality = '';
  private greenHideAt = 0;
  private resultShown = false;
  /** Direct mode (benchmark, ?autopilot): no menu, no countdown. */
  direct = false;
  /** CPU time of the game logic after the session's frame (ms, for the benchmark). */
  logicMs = 0;
  /** Ghost of the personal best: recorded this run, replayed from the stored best. */
  private readonly recorder = new GhostRecorder();
  private ghostTrack: GhostTrack | null = null;
  private readonly pose: GhostPose = { x: 0, y: 0, z: 0, qx: 0, qy: 0, qz: 0, qw: 1 };
  ghostShown = false;
  private readonly ctx: MenuContext;

  constructor(deps: FlowDeps) {
    this.d = deps;
    const st = stageLayout(deps.session.stage.spec);
    this.gridS = st.startS - GRID_BACK;
    this.stopS = st.stopS;
    this.timing = new StageTiming(st);
    this.ctx = {
      title: deps.title,
      tagline: deps.tagline,
      stageName: deps.stageName,
      stageKm: Number.isFinite(st.finishS) ? (st.finishS - st.startS) / 1000 : 0,
      carName: deps.carName,
      carLine: deps.carLine,
      cars: deps.cars,
      stages: deps.stages,
      carId: deps.carId,
      stageId: deps.stageId,
      selectContent: (kind, id) => {
        // A different car or stage is a new session: remember it and reload.
        deps.settings[kind] = id;
        deps.store.save(deps.settings);
        const url = new URL(location.href);
        url.searchParams.delete(kind);
        location.replace(url.toString());
      },
      settings: deps.settings,
      detectedPreset: deps.detectedPreset,
      best: () => deps.best.get(deps.stageId, deps.carId),
      changed: () => this.settingsChanged(),
      drive: () => this.drive(),
      restart: () => this.startStage(),
      resetToRoad: () => {
        this.resume();
        this.requestReset(false);
      },
      resume: () => this.resume(),
      quitToMenu: () => this.enterMenu(),
      fullscreen: () => toggleFullscreen(),
      stageTime: () => formatTime(this.timing.elapsed(deps.session.simTime)),
      captureKey: (cb) => deps.screens.captureKey(cb),
      capturePad: (kind, cb) => this.startCapture(kind, cb),
    };
    deps.devices.onPadConnected = (id) => toast(deps.ui, `Controller ready — ${id.replace(/\s*\(.*$/, '').slice(0, 40)}`);
    deps.session.onReset = (s) => deps.codriver?.resync(s, deps.session.simTime);
    this.applySettings();
    const lost = () => {
      if (this.state === 'drive' && !this.paused && !deps.bench) this.pause();
    };
    window.addEventListener('blur', lost);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        lost();
        if (!deps.bench) {
          deps.session.paused = true;
          deps.mixer.suspend();
        }
      } else if (!this.paused) {
        deps.session.paused = false;
        deps.mixer.resume();
      }
    });
  }

  // ---- Controller calibration (P1-5) ----

  private capture: PadCapture | null = null;
  private captureDone: ((r: CaptureResult | null) => void) | null = null;

  private startCapture(kind: CaptureKind, cb: (r: CaptureResult | null) => void): void {
    this.capture = new PadCapture(kind);
    this.captureDone = cb;
    this.d.screens.capturing = true;
  }

  private pollCapture(dt: number): void {
    const c = this.capture!;
    const pads = (typeof navigator !== 'undefined' && navigator.getGamepads ? Array.from(navigator.getGamepads()) : []) as (PadSnapshot | null)[];
    if (!c.poll(Math.min(dt, 0.1), pads)) return;
    const cb = this.captureDone;
    this.capture = null;
    this.captureDone = null;
    this.d.screens.capturing = false;
    this.padNav.sync();
    cb?.(c.result);
  }

  // ---- Settings ----

  private settingsChanged(): void {
    this.d.store.save(this.d.settings);
    this.applySettings();
  }

  applySettings(): void {
    const { session, settings: s, hud, mixer, codriver, devices } = this.d;
    const q = this.d.qualityOverride(qualityFor(s.graphics, this.d.detectedPreset));
    const qk = JSON.stringify(q);
    if (qk !== this.lastQuality) {
      this.lastQuality = qk;
      session.setQuality(q);
      this.d.onQuality(q);
    }
    session.rig.settings.fov = { ...s.fov };
    session.rig.settings.reduceMotion = s.reduceMotion;
    if (this.state === 'drive' && !this.direct && session.rig.mode !== s.camera) session.rig.setMode(s.camera);
    session.unitsMph = s.units === 'mph';
    hud.setUnits(session.unitsMph);
    hud.setToggles(s.hud);
    mixer.setVolumes(s.volumes);
    codriver?.apply(s.codriver);
    session.sim.vehicle.assists = { ...s.assists };
    devices.padSettings = { ...s.pad };
    devices.keys = structuredClone(s.keys);
    devices.pad = structuredClone(s.padMap);
  }

  // ---- States ----

  enterMenu(): void {
    const { session, screens, hud, devices, codriver } = this.d;
    this.state = 'menu';
    this.paused = false;
    this.direct = false;
    session.paused = false;
    this.d.mixer.resume();
    codriver?.restart();
    session.placeAtDistance(this.gridS);
    session.autopilot = () => HOLD;
    session.rig.showcase = true;
    session.world.setStartLights(0, false);
    hud.reset();
    hud.setVisible(false);
    devices.enabled = false;
    this.setHorn(false);
    this.showGhost(false);
    screens.set(mainMenu(this.ctx, screens));
    if (this.d.touchOnly && !this.d.settings.touchNoticeSeen) {
      screens.push(
        touchNotice(() => {
          this.d.settings.touchNoticeSeen = true;
          this.settingsChanged();
          screens.pop();
        }),
      );
    }
    this.padNav.sync();
  }

  /** DRIVE from the menu: the controls card first on the very first run. */
  drive(): void {
    if (!this.d.settings.controlsSeen) {
      this.d.screens.push(controlsScreen(this.ctx, this.d.screens, 'DRIVE', () => this.startStage()));
      return;
    }
    this.startStage();
  }

  /** Car on the start line; the co-driver reads the opening notes, then the lights. */
  startStage(): void {
    const { session, screens, hud, devices, codriver, settings } = this.d;
    screens.set(null);
    this.state = 'drive';
    this.paused = false;
    this.direct = false;
    session.paused = false;
    this.d.mixer.resume();
    const bot = this.d.testBot;
    session.autopilot = bot ? (dt) => (this.timing.phase === 'running' || this.timing.phase === 'finished' ? bot(dt) : HOLD) : null;
    session.rig.showcase = false;
    session.rig.setMode(settings.camera);
    session.placeAtDistance(this.gridS);
    codriver?.restart();
    codriver?.setPaused(false);
    this.timing.reset(session.progressS);
    this.gridT = 0;
    this.finishedAt = -1;
    this.stoppedT = 0;
    this.resultShown = false;
    this.flipT = this.oobT = this.stuckT = 0;
    this.prevBest = this.d.best.get(this.d.stageId, this.d.carId);
    this.recorder.reset();
    this.d.wear?.reset();
    this.ghostTrack = settings.ghost ? this.d.ghosts.get(this.d.stageId, this.d.carId) : null;
    this.showGhost(false);
    session.world.setStartLights(0, false);
    hud.reset();
    hud.setVisible(true);
    devices.enabled = true;
    devices.clearPressed();
    // The GC checkpoint runs once the car has settled on the grid (stageLogic): the teleport's
    // foliage and grass rebuild garbage belongs before it, not in the stage's budget.
    this.gcPending = true;
  }

  /** Benchmark / dev autopilot: straight onto the stage, clock running. */
  startDirect(): void {
    const { session, screens, hud, devices, codriver } = this.d;
    screens.set(null);
    this.state = 'drive';
    this.direct = true;
    session.rig.showcase = false;
    codriver?.setPaused(false);
    this.timing.reset(session.progressS);
    this.timing.beginCountdown(session.simTime - 5);
    this.timing.update(session.simTime, session.progressS);
    hud.reset();
    hud.setVisible(true);
    devices.enabled = true;
  }

  pause(): void {
    const { session, screens, devices, codriver, mixer } = this.d;
    if (this.state !== 'drive' || this.paused) return;
    this.paused = true;
    session.paused = true;
    codriver?.setPaused(true);
    mixer.suspend();
    devices.enabled = false;
    this.setHorn(false);
    screens.set(pauseScreen(this.ctx, screens));
    this.padNav.sync();
  }

  resume(): void {
    const { session, screens, devices, codriver, mixer } = this.d;
    if (!this.paused) return;
    screens.set(null);
    this.paused = false;
    session.paused = false;
    codriver?.setPaused(false);
    mixer.resume();
    devices.enabled = true;
    devices.clearPressed();
  }

  private showResults(): void {
    const { session, screens, hud, devices, codriver } = this.d;
    const r = this.timing.finish;
    if (!r || this.resultShown) return;
    this.resultShown = true;
    this.state = 'results';
    session.autopilot = () => HOLD;
    session.rig.showcase = true;
    hud.setVisible(false);
    devices.enabled = false;
    this.setHorn(false);
    codriver?.say(['good'], 'Good.');
    const isBest = this.d.best.submit(this.d.stageId, this.d.carId, r, this.d.buildId);
    if (isBest && this.recorder.samples > 1) this.d.ghosts.set(this.d.stageId, this.d.carId, this.recorder.track());
    this.showGhost(false);
    const lines: string[] = [];
    const fs = this.timing.penalties.filter((p) => p.reason === 'false-start');
    const rs = this.timing.penalties.filter((p) => p.reason === 'reset');
    if (fs.length) lines.push(`False start  +${fs.reduce((a, p) => a + p.seconds, 0)} s`);
    if (rs.length) lines.push(`Reset to road ×${rs.length}  +${rs.reduce((a, p) => a + p.seconds, 0)} s`);
    screens.set(finishScreen(this.ctx, r, this.prevBest, isBest, lines));
    this.padNav.sync();
  }

  /** R / View: back on the road. Before the green light the car just goes back to the grid. */
  requestReset(auto: boolean): void {
    const { session, hud } = this.d;
    if (this.state !== 'drive') return;
    const ph = this.timing.phase;
    if (ph === 'grid' || ph === 'countdown') {
      session.placeAtDistance(this.gridS);
      return;
    }
    session.resetToRoad();
    this.flipT = this.oobT = this.stuckT = 0;
    hud.prompt(null);
    if (ph === 'running' && !this.direct) {
      this.timing.addPenalty('reset', RESET_PENALTY, session.simTime);
      hud.notice(`${auto ? 'AUTO RESET' : 'RESET TO ROAD'}  +${RESET_PENALTY} S`);
    }
  }

  // ---- Per frame ----

  frame(dt: number, now: number): void {
    const { session, devices, screens, hud, codriver, carAudio } = this.d;
    this.d.gc.step();
    if (this.capture) this.pollCapture(dt);
    else if (screens.open) this.padNav.poll(now, (k) => screens.input(k));
    const simBefore = session.simTime;
    session.frame(dt);
    const tLogic = performance.now();
    const simDt = session.simTime - simBefore;
    const v = session.sim.vehicle;
    const t = v.telemetry;

    if (this.state === 'drive' && !this.paused) {
      if (devices.consumePressed('pause') && !this.d.bench) {
        this.pause();
        return;
      }
      if (devices.consumePressed('reset')) this.requestReset(false);
      if (session.rig.mode !== this.d.settings.camera && !this.direct) {
        this.d.settings.camera = session.rig.mode;
        this.d.store.save(this.d.settings);
      }
      this.setHorn(devices.held('horn'));
      this.stageLogic(simDt, now);
      this.updateWear(simDt);
      this.recovery(simDt);
      this.rumble();
    }
    this.d.touch?.update(this.state === 'drive' && !this.paused, !this.d.settings.assists.autoGearbox);
    if (!session.paused) {
      const interior = session.rig.mode === 'cockpit' && !session.rig.showcase;
      carAudio.update(v, Math.min(dt, 0.1), interior, session.collision());
      const cam = session.rig.camera;
      const e = cam.matrixWorld.elements;
      carAudio.crowd(cam.position.x, cam.position.z, e[0], e[2], interior);
    }
    if (this.state === 'drive') codriver?.update(session.progressS, t.speed, session.simTime);
    codriver?.setCockpit(session.rig.mode === 'cockpit' && !session.rig.showcase && session.car.codriver !== null);
    if (this.state === 'drive') {
      hud.update({ time: this.timing.elapsed(session.simTime), penalties: this.timing.penaltySeconds, speed: t.speed, rpm: t.rpm, gear: t.gear, s: session.progressS, gLat: t.gLat, gLong: t.gLong, cockpit: session.rig.mode === 'cockpit', now });
      if (this.greenHideAt && now > this.greenHideAt) {
        this.greenHideAt = 0;
        hud.setLights(0, false, false);
        session.world.setStartLights(0, false);
      }
    }
    this.logicMs = performance.now() - tLogic;
  }

  private stageLogic(simDt: number, now: number): void {
    const { session, hud, codriver } = this.d;
    const tm = this.timing;
    if (tm.phase === 'grid' && !this.direct) {
      this.gridT += simDt;
      const wantVoice = !!codriver && this.d.settings.codriver.voice && !this.d.mixer.failed;
      const read = !wantVoice || (codriver!.audible && codriver!.preStartDone && session.simTime >= codriver!.busyUntil + 0.4);
      // The lights wait for the start-line GC checkpoint (src/game/gc-guard.ts), which itself waits
      // for V8's one-off post-load memory reducer: the reducer's GCs leave the old generation a tight
      // budget, the checkpoint's a generous one, so the stage runs without a full GC.
      const gc = this.d.gc;
      const now = performance.now();
      if (this.gcPending && this.gridT >= 1.5 && now >= gc.readyAt) {
        this.gcPending = false;
        // A stage run: up to ~10 minutes for a slow first run (sizes the GC headroom).
        gc.begin(600);
      }
      const ready = !this.gcPending && !gc.running;
      if (this.gridT >= 1.2 && (read || this.gridT > 8)) {
        if (ready) {
          if (this.startWait) hud.prompt(null);
          this.startWait = false;
          tm.beginCountdown(session.simTime);
        } else {
          // Seconds to the green light (checkpoint ~0.5 s, then the lights).
          const toGreen = Math.max(0, (gc.readyAt - now) / 1000) + 0.5 + LIGHTS * LIGHT_INTERVAL;
          if (toGreen > LIGHTS * LIGHT_INTERVAL + 1.5) {
            this.startWait = true;
            hud.prompt(`STAGE START IN ${Math.ceil(toGreen)}`);
          }
        }
      }
    }
    for (const e of tm.update(session.simTime, session.progressS)) {
      switch (e.kind) {
        case 'light':
          session.world.setStartLights(e.lit, false);
          hud.setLights(e.lit, false, true);
          codriver?.say([COUNT_WORDS[e.lit - 1]], COUNT_WORDS[e.lit - 1].toUpperCase());
          break;
        case 'green':
          session.world.setStartLights(0, true);
          hud.setLights(0, true, !this.direct);
          if (!this.direct) codriver?.say(['go'], 'GO!');
          this.greenHideAt = now + 1500;
          break;
        case 'false-start':
          hud.banner(`FALSE START  +${tm.penalties[tm.penalties.length - 1].seconds} S`, 'bad', now, 3000);
          break;
        case 'split': {
          const pb = this.prevBest;
          hud.split(e.index, e.time, pb ? e.time - pb.splits[e.index] : null, now);
          break;
        }
        case 'finish':
          this.recordPose(e.result.time, true);
          hud.banner(`FINISH  ${formatTime(e.result.total)}`, 'info', now, 5000);
          this.finishedAt = session.simTime;
          break;
      }
    }
    if (tm.phase === 'running' && !this.direct) {
      const t = tm.elapsed(session.simTime);
      this.recordPose(t, false);
      this.showGhost(!!this.ghostTrack && this.d.settings.ghost && this.ghostTrack.poseAt(t, this.pose));
      if (this.ghostShown) this.d.ghostView!.setPose(this.pose);
    } else if (this.ghostShown) this.showGhost(false);
    if (tm.phase === 'finished' && !this.direct) {
      const t = session.sim.vehicle.telemetry;
      this.stoppedT = t.speed < 1 ? this.stoppedT + simDt : 0;
      if (this.stoppedT > 0.6 || session.progressS > this.stopS + 15 || session.simTime - this.finishedAt > 14) this.showResults();
    }
  }

  private readonly livePose: GhostPose = { x: 0, y: 0, z: 0, qx: 0, qy: 0, qz: 0, qw: 1 };
  private readonly surfaceShare: Partial<Record<SurfaceId, number>> = {};
  private readonly prevVel = { x: 0, y: 0, z: 0 };
  private readonly impactLocal = { x: 0, y: 0, z: 0 };

  /** Dirt from the surfaces under the wheels, dents from hard impacts (P1-3). */
  private updateWear(simDt: number): void {
    const wear = this.d.wear;
    if (!wear || simDt <= 0) return;
    const { session } = this.d;
    const v = session.sim.vehicle;
    const share = this.surfaceShare;
    for (const k in share) share[k as SurfaceId] = 0;
    for (const w of v.wheels) if (w.contact) share[w.surface] = (share[w.surface] ?? 0) + 0.25;
    wear.state.drive(v.telemetry.speed * simDt, share);
    const f = session.impactLastFrame;
    if (f > 0) {
      // Rapier's force direction has an arbitrary sign: the side that hit is the one the car was
      // moving toward just before the impact.
      const d = session.impactDir;
      const pv = this.prevVel;
      const sgn = d.x * pv.x + d.y * pv.y + d.z * pv.z >= 0 ? 1 : -1;
      // World → car-local: rotate by the inverse of the body rotation.
      const q = v.rot;
      const x = d.x * sgn;
      const y = d.y * sgn;
      const z = d.z * sgn;
      const tx = 2 * (-q.y * z + q.z * y);
      const ty = 2 * (-q.z * x + q.x * z);
      const tz = 2 * (-q.x * y + q.y * x);
      const out = this.impactLocal;
      out.x = x + q.w * tx + (-q.y * tz + q.z * ty);
      out.y = y + q.w * ty + (-q.z * tx + q.x * tz);
      out.z = z + q.w * tz + (-q.x * ty + q.y * tx);
      wear.state.impact(f, out);
    }
    this.prevVel.x = v.linvel.x;
    this.prevVel.y = v.linvel.y;
    this.prevVel.z = v.linvel.z;
    wear.sync();
  }

  private recordPose(t: number, force: boolean): void {
    const v = this.d.session.sim.vehicle;
    const p = this.livePose;
    p.x = v.pos.x;
    p.y = v.pos.y;
    p.z = v.pos.z;
    p.qx = v.rot.x;
    p.qy = v.rot.y;
    p.qz = v.rot.z;
    p.qw = v.rot.w;
    this.recorder.sample(t, p, force);
  }

  private showGhost(on: boolean): void {
    const g = this.d.ghostView;
    on = on && !!g;
    if (on === this.ghostShown) return;
    this.ghostShown = on;
    if (g) g.root.visible = on;
  }

  /** Flipped, far off the road or stuck: offer a reset, and apply it when it's hopeless. */
  private recovery(simDt: number): void {
    const { session, hud } = this.d;
    if (this.timing.phase !== 'running' || this.direct) {
      hud.prompt(null);
      return;
    }
    const v = session.sim.vehicle;
    const tr = session.stage.track;
    const i = tr ? Math.max(0, Math.min(tr.width.length - 1, Math.round(session.progressS))) : 0;
    const off = tr ? Math.abs(session.lateral) - tr.width[i] / 2 : 0;
    this.flipT = v.up.y < 0.35 ? this.flipT + simDt : 0;
    this.oobT = off > 25 ? this.oobT + simDt : 0;
    this.stuckT = v.telemetry.speed < 0.7 ? this.stuckT + simDt : 0;
    if (this.flipT > 3 || this.oobT > 2.5) {
      this.requestReset(true);
      return;
    }
    const pad = this.d.devices.lastDevice === 'gamepad';
    if (this.flipT > 0.6 || off > 12 || this.stuckT > 4) hud.prompt(`${pad ? 'VIEW' : 'R'} — RESET TO ROAD (+${RESET_PENALTY} S)`);
    else hud.prompt(null);
  }

  private rumble(): void {
    const { session, devices } = this.d;
    const imp = session.impactLastFrame;
    if (imp > 12000) devices.rumble(Math.min(1, imp / 60000), Math.min(1, imp / 30000), 160);
    const w = session.sim.vehicle.wheels[0];
    const surf = w.contact ? w.surface : this.lastSurface;
    if (surf !== this.lastSurface) {
      if (this.lastSurface && session.sim.vehicle.telemetry.speed > 3) devices.rumble(0.05, 0.3, 90);
      this.lastSurface = surf;
    }
  }

  private setHorn(on: boolean): void {
    if (on === this.hornOn) return;
    this.hornOn = on;
    this.d.carAudio.setHorn(on);
  }
}

function toggleFullscreen(): void {
  const doc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
  const el = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
  try {
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      if (doc.exitFullscreen) void doc.exitFullscreen().catch(() => undefined);
      else doc.webkitExitFullscreen?.();
    } else if (el.requestFullscreen) void el.requestFullscreen({ navigationUI: 'hide' }).catch(() => undefined);
    else el.webkitRequestFullscreen?.();
  } catch {
    /* fullscreen not allowed here */
  }
}

